import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileDown,
  Loader2,
  Printer,
  Search,
  ShieldAlert,
  IdCard,
  Users,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import {
  downloadBadgesCsv,
  downloadBadgesPdf,
  fetchBadgePdfBlob,
  printBadgePdf,
} from '@/lib/badges';
import type { Registration } from '@/types';

type RegistrationsResponse = {
  success: boolean;
  data: Registration[];
};

export default function BadgeExportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { event: activeEvent, loading: activeEventLoading } = useActiveEvent();

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkPdfBusy, setBulkPdfBusy] = useState(false);
  const [bulkCsvBusy, setBulkCsvBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedReg, setSelectedReg] = useState<Registration | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const isAuthorized = user?.roles?.some((r) => r === 'admin' || r === 'staff') ?? false;

  useEffect(() => {
    if (!user) return;
    if (!isAuthorized) {
      toast.error('Access denied — admin or staff role required.');
      navigate('/');
    }
  }, [user, isAuthorized, navigate]);

  const fetchRegistrations = useCallback(async (eventId: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '1000', status: 'approved', eventId });
      const res = await api.get<RegistrationsResponse>(`/admin/registrations?${params.toString()}`);
      if (res.success) {
        setRegistrations(res.data.filter((r) => r.status === 'approved' && r.profileId));
      }
    } catch {
      toast.error('Failed to load approved attendees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthorized) return;
    if (activeEventLoading) return;
    if (!activeEvent?.id) {
      setRegistrations([]);
      setLoading(false);
      return;
    }
    fetchRegistrations(activeEvent.id);
  }, [isAuthorized, activeEvent?.id, activeEventLoading, fetchRegistrations]);

  // Load preview when the selection changes.
  useEffect(() => {
    if (!selectedReg?.profileId) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    let urlToRevoke: string | null = null;
    setPreviewLoading(true);
    fetchBadgePdfBlob([selectedReg.profileId])
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        urlToRevoke = url;
        setPreviewUrl(url);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load badge preview';
        toast.error(message);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [selectedReg?.profileId]);

  const filteredAttendees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return registrations;
    return registrations.filter((r) => {
      const haystack = `${r.firstName} ${r.lastName} ${r.email} ${r.organization ?? ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [registrations, search]);

  async function handleBulkPdf() {
    setBulkPdfBusy(true);
    try {
      await downloadBadgesPdf();
      toast.success('Badge PDF downloaded');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate badge PDF';
      toast.error(message);
    } finally {
      setBulkPdfBusy(false);
    }
  }

  async function handleBulkCsv() {
    setBulkCsvBusy(true);
    try {
      await downloadBadgesCsv();
      toast.success('Badge CSV downloaded');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate badge CSV';
      toast.error(message);
    } finally {
      setBulkCsvBusy(false);
    }
  }

  async function handlePrint() {
    if (!selectedReg?.profileId) return;
    setPrinting(true);
    try {
      await printBadgePdf([selectedReg.profileId]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open print dialog';
      toast.error(message);
    } finally {
      setPrinting(false);
    }
  }

  if (!isAuthorized) {
    return (
      <DesktopShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <ShieldAlert className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Access denied</p>
        </div>
      </DesktopShell>
    );
  }

  const LeftPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileDown className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Bulk Export</h2>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : `${registrations.length} approved attendees`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <IdCard className="h-4 w-4" />
            Export all approved attendees
          </CardTitle>
          <CardDescription className="text-xs">
            {loading ? (
              <Skeleton className="h-3 w-24" />
            ) : (
              <span>
                <Badge variant="secondary" className="h-5">
                  {registrations.length}
                </Badge>{' '}
                badges ready to print
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button
            onClick={handleBulkPdf}
            disabled={loading || bulkPdfBusy || bulkCsvBusy || registrations.length === 0}
            className="w-full"
            size="sm"
          >
            {bulkPdfBusy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Building PDF…
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Export PDF
              </>
            )}
          </Button>
          <Button
            onClick={handleBulkCsv}
            disabled={loading || bulkPdfBusy || bulkCsvBusy || registrations.length === 0}
            className="w-full"
            size="sm"
          >
            {bulkCsvBusy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Building CSV…
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">About badges</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>
            Each badge measures <strong>4×3 inches</strong> and is designed for the Epson
            PictureMate PM-400.
          </p>
          <p>
            The QR code on the badge matches the attendee's in-app code, so scanning a printed
            badge with the staff scanner will check the attendee in.
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );

  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Attendees</h2>
            <p className="text-sm text-muted-foreground">
              {loading
                ? 'Loading…'
                : `${filteredAttendees.length} of ${registrations.length} approved`}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by name, email, or organization"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            aria-label="Search attendees"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : filteredAttendees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <IdCard className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {search.trim() ? 'No matching attendees' : 'No approved attendees yet'}
            </p>
          </div>
        ) : (
          <ul className="divide-y">
            {filteredAttendees.map((reg) => {
              const isSelected = selectedReg?.id === reg.id;
              return (
                <li
                  key={reg.id}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-accent/10 border-l-2 border-l-accent' : 'hover:bg-muted/50 border-l-2 border-l-transparent'
                  }`}
                  onClick={() => setSelectedReg(reg)}
                >
                  <p className={`text-sm truncate ${isSelected ? 'font-semibold' : 'font-medium'}`}>
                    {reg.firstName} {reg.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {reg.organization || reg.email}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  const RightPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Eye className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Preview</h2>
            <p className="text-sm text-muted-foreground truncate">
              {selectedReg
                ? `${selectedReg.firstName} ${selectedReg.lastName}`
                : 'Select an attendee'}
            </p>
          </div>
        </div>
      </div>

      {!selectedReg ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
          <IdCard className="h-10 w-10 mb-3" />
          <p className="text-sm">Select an attendee from the list to preview their badge</p>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-auto p-6 flex items-start justify-center bg-muted/20">
            {previewLoading || !previewUrl ? (
              <div className="flex items-center gap-2 text-muted-foreground mt-12">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading preview…</span>
              </div>
            ) : (
              <object
                type="application/pdf"
                data={`${previewUrl}#zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`}
                className="border bg-white shadow"
                style={{ width: '100%', aspectRatio: '4 / 3', maxWidth: 480 }}
                aria-label={`Badge preview for ${selectedReg.firstName} ${selectedReg.lastName}`}
              >
                <p className="text-xs text-muted-foreground p-3">
                  Inline preview unavailable in this browser.{' '}
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="underline">
                    Open the PDF in a new tab
                  </a>
                  .
                </p>
              </object>
            )}
          </div>

          <Separator />
          <div className="p-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setSelectedReg(null)}
              disabled={printing}
              className="flex-1"
            >
              Clear
            </Button>
            <Button
              onClick={handlePrint}
              disabled={previewLoading || printing || !previewUrl}
              className="flex-1"
            >
              {printing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Opening printer…
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4 mr-2" />
                  Print Badge
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <DesktopShell>
      <div className="h-full flex flex-col">
        <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h1 className="text-2xl font-bold">Badge Export</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeEventLoading
              ? 'Loading event…'
              : activeEvent?.name
                ? `Showing attendees for ${activeEvent.name} only`
                : 'No active event — set an event to "active" to print badges'}
          </p>
        </div>

        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            left={LeftPanel}
            center={CenterPanel}
            right={RightPanel}
            leftWidth="260px"
            equalCenterRight
            connectedPanels={!!selectedReg}
          />
        </div>
      </div>
    </DesktopShell>
  );
}
